import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getPublicToursByUser, getPublicToursByUserLiked } from '../../../utils/Vanlifer/tour';
import { TourOverview } from '../../../services/restserver-openapi';
import { mapErrorMessages } from '../../../utils/errorHandling';
import { asyncToggleTourLike } from "../tourSlice";

type ProfileToursState = {
    loading: boolean;
    error: string | null;
    publicTours?: TourOverview[] | null;
    likedTours?: TourOverview[] | null;
};

const initialState: ProfileToursState = {
    loading: false,
    error: null,
    publicTours: null,
    likedTours: null,
};

export const asyncGetPublicToursForVanlifer = createAsyncThunk(
    'profileTours/getPublicToursForVanlifer',
    async (communityName: string) => {
        return getPublicToursByUser(communityName);
    }
);

export const asyncGetMostLikedTours = createAsyncThunk(
    'profileTours/getMostLikedTours',
    async (communityName: string) => {
        return getPublicToursByUserLiked(communityName);
    }
);

export const profileToursSlice = createSlice({
    name: 'profileTours',
    initialState,
    reducers: {
        resetProfileToursState: (state) => {
            state.loading = false;
            state.error = null;
            state.publicTours = undefined;
            state.likedTours = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(asyncGetPublicToursForVanlifer.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncGetPublicToursForVanlifer.fulfilled, (state, { payload }) => {
                state.publicTours = payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(asyncGetPublicToursForVanlifer.rejected, (state, action) => {
                state.loading = false;
                if (action.error.message) {
                    state.error = mapErrorMessages({ errorMessage: action.error.message });
                }
            })
            .addCase(asyncGetMostLikedTours.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncGetMostLikedTours.fulfilled, (state, { payload }) => {
                state.likedTours = payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(asyncGetMostLikedTours.rejected, (state, action) => {
                state.loading = false;
                if (action.error.message) {
                    state.error = mapErrorMessages({ errorMessage: action.error.message });
                }
            }).addCase(asyncToggleTourLike.pending, (state, { meta }) => {
            const { tourId, isLiked } = meta.arg;

            state.publicTours?.forEach((tour) => {
                if (tour.tourId === tourId) {
                    if (typeof tour.liked === 'undefined') {
                        tour.liked = isLiked;
                        tour.likeCount = isLiked ? 1 : 0;
                    } else if (tour.liked !== isLiked) {
                        tour.liked = isLiked;
                        tour.likeCount = (tour.likeCount || 0) + (isLiked ? 1 : -1);
                    }
                }
            });

            state.likedTours?.forEach((tour) => {
                if (tour.tourId === tourId) {
                    if (typeof tour.liked === 'undefined') {
                        tour.liked = isLiked;
                        tour.likeCount = isLiked ? 1 : 0;
                    } else if (tour.liked !== isLiked) {
                        tour.liked = isLiked;
                        tour.likeCount = (tour.likeCount || 0) + (isLiked ? 1 : -1);
                    }
                }
            });
        }).addCase(asyncToggleTourLike.rejected, (state, { meta }) => {
            const { tourId, isLiked } = meta.arg;

            state.publicTours?.forEach((tour) => {
                if (tour.tourId === tourId) {
                    tour.liked = !isLiked;
                    tour.likeCount = (tour.likeCount || 0) + (isLiked ? -1 : 1);
                }
            });

            state.likedTours?.forEach((tour) => {
                if (tour.tourId === tourId) {
                    tour.liked = !isLiked;
                    tour.likeCount = (tour.likeCount || 0) + (isLiked ? -1 : 1);
                }
            });
        });
    }
});

export const { resetProfileToursState } = profileToursSlice.actions;

export const profileToursReducer = profileToursSlice.reducer;
